import axios, { AxiosInstance, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from "axios";

// Local CRA (:3001) and the backend-served build (:443) both talk to the local API.
// Do not send credentials or a JSON Content-Type on GETs: that combination
// triggers a CORS preflight that the backend used to reject from :3001.
const isLocal: boolean =
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1";
const baseURL: string = isLocal
  ? "http://localhost:443/api"
  : "https://finally.cc/api";

const instance: AxiosInstance = axios.create({
  baseURL: baseURL,
  timeout: 15000,
  withCredentials: false,
});

// Request interceptor
instance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem("authToken");
    if (token && token.split(".").length === 3) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error: AxiosError) => {
    console.error('Request error:', error.message);
    return Promise.reject(error);
  }
);

// Response interceptor
instance.interceptors.response.use(
  (response: AxiosResponse) => {
    // Check if the content-type is text/html instead of application/json
    const contentType = String(response.headers["content-type"] ?? "");
    
    if (contentType.includes("text/html") && typeof response.data === "string" && response.data.includes("<!doctype html>")) {
      // This is likely an HTML response when we expected JSON
      console.error("Received HTML response instead of JSON:", response.config.url);
      
      // Create a custom error with meaningful message
      const error = new Error("API returned HTML instead of JSON. You may need to check your server connection or authentication.") as AxiosError;
      error.response = response;
      
      // Reject the promise so it goes to our catch handler
      return Promise.reject(error);
    }
    
    return response;
  },
  (error: AxiosError) => {
    // Handle different types of errors with appropriate messages
    let errorMessage = "An unknown error occurred";
    
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      const status = error.response.status;
      
      switch (status) {
        case 400:
          errorMessage = "Bad request. Please check your inputs.";
          break;
        case 401:
          errorMessage = "Unauthorized. Please log in again.";
          // You could trigger a logout or redirect to login here
          break;
        case 403:
          errorMessage = "Forbidden. You don't have permission to access this resource.";
          break;
        case 404:
          errorMessage = "Resource not found.";
          break;
        case 500:
          errorMessage = "Server error: The server encountered an issue. Please try again later.";
          break;
        default:
          errorMessage = `Server responded with status code ${status}`;
      }
      
      console.error("API response error:", {
        status: error.response.status,
        data: error.response.data,
        headers: error.response.headers,
        url: error.config?.url
      });
    } else if (error.request) {
      // The request was made but no response was received
      if (error.code === 'ECONNABORTED') {
        errorMessage = "Request timeout. Please check your internet connection and try again.";
      } else {
        errorMessage = "No response received from server. Please check your internet connection.";
      }
      console.error("No response received:", error.request);
    } else {
      // Something happened in setting up the request that triggered an Error
      errorMessage = error.message;
      console.error("Request setup error:", error.message);
    }
    
    // You can dispatch to a global error state here if needed
    // For example: store.dispatch(setGlobalError(errorMessage));
    
    error.message = errorMessage;
    return Promise.reject(error);
  }
);

export default instance; 